import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
};
const oauth = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  head: () => ({ meta: [{ title: "ربط Claude — د. سارة النحّاس" }, { name: "robots", content: "noindex" }] }),
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s.authorization_id === "string" ? s.authorization_id : "",
  }),
  beforeLoad: async ({ search, location }) => {
    if (!search.authorization_id) throw new Error("طلب الربط غير صالح أو منتهي الصلاحية.");
    const { data } = await supabase.auth.getUser();
    if (!data.user || data.user.is_anonymous) {
      throw redirect({ to: "/auth", search: { redirect: location.pathname + location.searchStr } });
    }
    return { email: data.user.email ?? "" };
  },
  loader: async ({ location }) => {
    const id = new URLSearchParams(location.search).get("authorization_id")!;
    const { data, error } = await oauth().getAuthorizationDetails(id);
    if (error) throw new Error("طلب الربط غير صالح أو منتهي الصلاحية.");
    const immediate = data?.redirect_url ?? data?.redirect_to;
    if (immediate && !data?.client) throw redirect({ href: immediate });
    return data;
  },
  pendingComponent: () => (
    <main dir="rtl" className="min-h-screen bg-page px-4 py-16">
      <div className="mx-auto max-w-md h-48 animate-pulse rounded-ds-xl bg-cream-200" />
    </main>
  ),
  errorComponent: ({ error }) => (
    <main dir="rtl" className="min-h-screen bg-page px-4 py-16 text-center"><div className="mx-auto max-w-md">
      <h1 className="font-display text-xl font-bold text-brown-900">تعذّر فتح طلب الربط</h1>
      <p className="mt-2 text-sm text-brown-500">
        {error instanceof Error ? error.message : "حدث خطأ غير متوقّع."} المحاولة من جديد ممكنة من Claude.
      </p>
      </div>
    </main>
  ),
  component: Consent,
});

function Consent() {
  const details = Route.useLoaderData();
  const { email } = Route.useRouteContext();
  const { authorization_id } = Route.useSearch();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const client = details?.client?.name ?? "Claude";

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    const { data, error } = approve
      ? await oauth().approveAuthorization(authorization_id)
      : await oauth().denyAuthorization(authorization_id);
    const target = data?.redirect_url ?? data?.redirect_to;
    if (error || !target) {
      setBusy(false);
      setError("ما اكتمل الطلب. المحاولة من جديد ممكنة.");
      return;
    }
    window.location.href = target;
  }

  return (
    <main dir="rtl" className="min-h-screen bg-page px-4 py-16">
      <div className="mx-auto max-w-md rounded-ds-xl border border-hairline-subtle bg-surface p-8 shadow-sm">
        <h1 className="font-display text-xl font-bold text-brown-900">ربط {client} بمنصّة د. سارة النحّاس</h1>
        <p className="mt-2 text-sm text-brown-500">
          الحساب: <span dir="ltr">{email}</span>
        </p>
        <p className="mt-4 text-base text-brown-900">
          سيتمكّن {client} من قراءة كل بيانات الإدارة: الحجوزات، العملاء، نماذج الحالات، المدونة، الدورات، المدفوعات، والبوصلة. لا يستطيع تعديل أي شيء أو حذفه أو إرساله.
        </p>
        <p className="mt-3 text-sm text-brown-500">لا يتجاوز هذا الربط صلاحيات حسابك.</p>
        {error && (
          <p role="alert" className="mt-4 text-sm text-coral-700">{error}</p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button disabled={busy} onClick={() => decide(true)} className="min-h-12 rounded-full bg-primary px-6 font-display font-bold text-brown-900 hover:bg-primary-hover">
            السماح بالربط
          </Button>
          <Button disabled={busy} variant="outline" onClick={() => decide(false)} className="min-h-12 rounded-full border-hairline-default bg-surface px-6 font-display font-bold text-brown-900 hover:bg-cream-100">
            إلغاء الربط
          </Button>
        </div>
      </div>
    </main>
  );
}
